import { useEffect, useRef, type CSSProperties } from "react";
import lottie from "lottie-web";
import animationData from "../assets/camera.json";

interface Props {
  size?: number;
  style?: CSSProperties;
  className?: string;
}

/**
 * The user's "Camera_Line icon" Lottie, played 100% AS-IS via lottie-web (original shapes + animation,
 * unmodified JSON). Only the colour is applied from the outside (CSS: .lottie-cam svg path → currentColor)
 * so the file itself is untouched. The viewBox is cropped to the camera so it fills the box (bigger).
 * prefers-reduced-motion holds the final frame instead of animating.
 */
export function LottieCamera({ size = 56, style, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    const anim = lottie.loadAnimation({
      container: el,
      renderer: "svg",
      loop: true,
      autoplay: !reduce,
      animationData,
    });
    anim.addEventListener("DOMLoaded", () => {
      const svg = el.querySelector("svg");
      if (svg) {
        svg.setAttribute("viewBox", "20 60 360 300"); // crop the empty margins → camera fills the box
        svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      }
      if (reduce) anim.goToAndStop(anim.totalFrames - 1, true);
    });
    return () => anim.destroy();
  }, []);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`lottie-cam${className ? " " + className : ""}`}
      style={{ width: size, height: size, color: "var(--ink)", ...style }}
    />
  );
}
